'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';

import { resetPassword } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { resetPasswordForm } from '@eliadmin/core/src/components/auth/reset-password-schema';
import { PasswordField, TextField } from '@eliadmin/core/src/components/svetech/forms/fields';
import { AuthHeader } from '@eliadmin/core/src/components/svetech/forms/form-parts';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export { resetPasswordForm } from '@eliadmin/core/src/components/auth/reset-password-schema';

/**
 * New password from the e-mail link (core logic unchanged: toast and back to sign-in): H2
 * title, e-mail, the new password twice with show / hide, the full-width submit.
 */
export function ResetPasswordForm({ token }: { token: string }) {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof resetPasswordForm>>>({
    resolver: zodResolver(resetPasswordForm(formValidationMessages)),
    defaultValues: {
      email: '',
      password: '',
      password_confirmation: '',
    },
  });

  const { toast } = useToast();
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {
      await resetPassword({ ...data, token, locale });
      toast({
        title: t('text649'),
        variant: 'success',
      });
      router.push(ROUTES.SIGN_IN());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text651'),
        description: t('text3140'),
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        noValidate
        className={cn('flex w-full flex-col gap-6 md:gap-8', 'ea-reset-password-form')}
        onSubmit={handleSubmit}
      >
        <div className="ea-reset-password-form__header">
          <AuthHeader title={t('text1696')} />
        </div>

        <div className="ea-reset-password-form__content flex flex-col gap-4">
          <TextField
            control={form.control}
            name="email"
            type="email"
            autoComplete="email"
            label={t('text35')}
            placeholder={t('text35')}
            required
            className="ea-reset-password-form__field-email"
          />
          <PasswordField
            control={form.control}
            name="password"
            autoComplete="new-password"
            label={t('text191')}
            placeholder={t('text191')}
            required
            className="ea-reset-password-form__field-password-new"
          />
          <PasswordField
            control={form.control}
            name="password_confirmation"
            autoComplete="new-password"
            label={t('text1108')}
            placeholder={t('text1108')}
            required
            className="ea-reset-password-form__field-password-new-repeat"
          />
        </div>

        <Button
          type="submit"
          className="ea-reset-password-form__submit-button w-full"
          isLoading={form.formState.isSubmitting}
        >
          {t('text187')}
        </Button>
      </form>
    </Form>
  );
}
